import Logger from '@/lib/logger';
import asyncTimeout from '@discover-lib/asyncTimeout';

const logger = new Logger('BATCHER');
// logger.enable();

/**
 * Creates a batching function that collects elements over a time period before processing them as a batch.
 *
 * @param processBatch - Function that processes the collected batch elements
 * @param period - Time in milliseconds to collect elements before processing the batch
 * @returns A function that adds elements to the current batch and returns a promise that resolves when the batch is processed
 *
 * @example
 * const processBatch = (items) => {
 *   // Process array of batched items
 * };
 *
 * const addToBatch = batcher(processBatch, 1000); // 1 second batch window
 *
 * // Add items to batch
 * addToBatch(item1).then(() => { console.log('item1 processed') });
 * addToBatch(item2).then(() => { console.log('item2 processed') });
 * // Items will be processed together after 1 second
 */
export default function batcher<BatchElement>(
  processBatch: (_currentBatch: BatchElement[]) => void,
  period: number
) {
  let currentBatch: BatchElement[] = [];
  let batchPromise: Promise<void> | undefined;

  function reset() {
    currentBatch = [];
    batchPromise = undefined;
  }

  function addToBatch(batchElement: BatchElement) {
    logger.log('Add to batch');

    currentBatch.push(batchElement);

    if (!batchPromise) {
      batchPromise = asyncTimeout(() => {
        logger.log('Process batch', currentBatch.length);

        processBatch(currentBatch);
        reset();
      }, period);
    }

    return batchPromise;
  }

  return addToBatch;
}
